import React, { useMemo, useRef, useState } from "react";
import { bulkAddGuests } from "../api/rsvp";
import {
  TICKET_TIER_DESCRIPTIONS,
  TICKET_TIER_LABELS,
  GuestTicketTier,
  GuestWithRsvp,
  GUEST_TIER_OPTIONS,
} from "../types";
import {
  applyDefaultsToGuests,
  CsvGuestDraft,
  parseGuestCsv,
  validateAllGuestDrafts,
} from "../utils/csvGuests";

interface Props {
  weddingId: string;
  existingGuests?: GuestWithRsvp[];
  onSuccess: (emailWarning?: string | null) => void;
}

type UploadStep = "upload" | "review";

const GuestCsvUpload: React.FC<Props> = ({ weddingId, existingGuests = [], onSuccess }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<UploadStep>("upload");
  const [guests, setGuests] = useState<CsvGuestDraft[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [defaultTier, setDefaultTier] = useState<GuestTicketTier>("party_not_presents");
  const [defaultPlusOneLimit, setDefaultPlusOneLimit] = useState(0);
  const [customizePlusOnes, setCustomizePlusOnes] = useState(false);

  const preparedGuests = useMemo(
    () =>
      applyDefaultsToGuests(
        guests,
        { ticket_tier: defaultTier, plus_one_limit: defaultPlusOneLimit },
        customizePlusOnes
      ),
    [guests, defaultTier, defaultPlusOneLimit, customizePlusOnes]
  );

  const { rowErrors, hasErrors, isValid } = useMemo(
    () => validateAllGuestDrafts(preparedGuests, existingGuests),
    [preparedGuests, existingGuests]
  );

  const resetUpload = () => {
    setStep("upload");
    setGuests([]);
    setParseErrors([]);
    setSubmitError("");
    setDefaultTier("party_not_presents");
    setDefaultPlusOneLimit(0);
    setCustomizePlusOnes(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSubmitError("");
    setParseErrors([]);

    try {
      const text = await file.text();
      const result = parseGuestCsv(text);

      if (result.errors.length > 0) {
        setParseErrors(result.errors);
        setGuests([]);
        setStep("upload");
        return;
      }

      setGuests(result.guests);
      setStep("review");
    } catch (err) {
      setParseErrors([err instanceof Error ? err.message : "Failed to read CSV file"]);
      setGuests([]);
      setStep("upload");
    }
  };

  const updateGuest = (id: string, updates: Partial<CsvGuestDraft>) => {
    setGuests((current) =>
      current.map((guest) => (guest.id === id ? { ...guest, ...updates } : guest))
    );
  };

  const handleSubmit = async () => {
    if (!isValid) return;

    setSubmitting(true);
    setSubmitError("");

    try {
      const { data } = await bulkAddGuests({
        wedding_id: weddingId,
        guests: preparedGuests.map((guest) => ({
          full_name: guest.full_name.trim(),
          email: guest.email.trim() || undefined,
          ticket_tier: guest.ticket_tier,
          plus_one_limit: guest.plus_one_limit,
        })),
      });
      resetUpload();
      onSuccess(data.email_warning);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to send invites");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="guest-csv-upload form">
      <h4>Upload Guest List (CSV)</h4>
      <p className="form-hint">
        CSV must include a <strong>full_name</strong> or <strong>name</strong> column.
        An <strong>email</strong> column is optional.
      </p>

      {step === "upload" && (
        <>
          <div className="form-group">
            <label htmlFor="guest_csv">CSV File</label>
            <input
              id="guest_csv"
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
            />
          </div>

          {parseErrors.length > 0 && (
            <div className="error-message inline">
              {parseErrors.map((message) => (
                <p key={message}>{message}</p>
              ))}
            </div>
          )}
        </>
      )}

      {step === "review" && (
        <>
          <div className="csv-review-summary">
            <p className="form-hint">
              Review {preparedGuests.length} guest
              {preparedGuests.length === 1 ? "" : "s"} before sending invites.
            </p>
            <button type="button" className="btn btn-secondary btn-sm" onClick={resetUpload}>
              Choose a Different File
            </button>
          </div>

          <div className="form-group">
            <label htmlFor="csv_default_tier">Default Ticket Tier</label>
            <select
              id="csv_default_tier"
              value={defaultTier}
              onChange={(e) => {
                const tier = e.target.value as GuestTicketTier;
                setDefaultTier(tier);
                if (tier !== "inner_circle_plus_more") {
                  setCustomizePlusOnes(false);
                }
              }}
            >
              {GUEST_TIER_OPTIONS.map((tier) => (
                <option key={tier} value={tier}>
                  {TICKET_TIER_LABELS[tier]}
                </option>
              ))}
            </select>
            <p className="form-hint">{TICKET_TIER_DESCRIPTIONS[defaultTier]}</p>
          </div>

          {defaultTier === "inner_circle_plus_more" && (
            <>
              <div className="form-group">
                <label htmlFor="csv_default_plus_one">Default Plus-One Allowance</label>
                <input
                  id="csv_default_plus_one"
                  type="number"
                  min={0}
                  max={10}
                  value={defaultPlusOneLimit}
                  disabled={customizePlusOnes}
                  onChange={(e) =>
                    setDefaultPlusOneLimit(parseInt(e.target.value || "0", 10))
                  }
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={customizePlusOnes}
                    onChange={(e) => setCustomizePlusOnes(e.target.checked)}
                  />
                  Customize plus-one allowance per guest
                </label>
              </div>
            </>
          )}

          <div className="guest-table-wrap">
            <table className="guest-table csv-review-table">
              <thead>
                <tr>
                  <th>Full Name</th>
                  <th>Email</th>
                  {defaultTier === "inner_circle_plus_more" && customizePlusOnes && (
                    <th>Plus-Ones</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {preparedGuests.map((guest, index) => {
                  const errors = rowErrors[index];
                  return (
                    <tr key={guest.id} className={errors && Object.keys(errors).length ? "row-error" : ""}>
                      <td>
                        <input
                          value={guest.full_name}
                          onChange={(e) =>
                            updateGuest(guest.id, { full_name: e.target.value })
                          }
                          aria-invalid={Boolean(errors.full_name)}
                        />
                        {errors.full_name && (
                          <p className="field-error">{errors.full_name}</p>
                        )}
                      </td>
                      <td>
                        <input
                          type="email"
                          value={guest.email}
                          onChange={(e) => updateGuest(guest.id, { email: e.target.value })}
                          aria-invalid={Boolean(errors.email)}
                        />
                        {errors.email && <p className="field-error">{errors.email}</p>}
                      </td>
                      {defaultTier === "inner_circle_plus_more" && customizePlusOnes && (
                        <td>
                          <input
                            type="number"
                            min={0}
                            max={10}
                            value={guest.plus_one_limit}
                            onChange={(e) =>
                              updateGuest(guest.id, {
                                plus_one_limit: parseInt(e.target.value || "0", 10),
                              })
                            }
                            aria-invalid={Boolean(errors.plus_one_limit)}
                          />
                          {errors.plus_one_limit && (
                            <p className="field-error">{errors.plus_one_limit}</p>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {hasErrors && (
            <p className="form-hint">
              Fix the highlighted issues before sending invites.
            </p>
          )}

          {submitError && <div className="error-message inline">{submitError}</div>}

          <div className="button-row">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!isValid || submitting}
              onClick={handleSubmit}
            >
              {submitting ? "Sending Invites..." : `Send ${preparedGuests.length} Invites`}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={submitting}
              onClick={resetUpload}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default GuestCsvUpload;
